import { IErrorResponse } from './main.interface';

/**
 * Notification types from backend NOTIFICATION_TYPES enum
 */
export type NotificationType =
  | 'EVENT'
  | 'MESSAGE'
  | 'ATTENDANCE'
  | 'INVOICE_REMINDER'
  | 'INVOICE_OVERDUE_REMINDER'
  | 'STUDENT_AWARD'
  | 'ATTENDANCE_ABSENT'
  | 'ATTENDANCE_CHECK_IN'
  | 'ATTENDANCE_CHECK_OUT'
  | 'ATTENDANCE_LATE_CHECK_IN'
  | 'ATTENDANCE_EARLY_CHECK_OUT'
  | 'STUDENT_ATTENDANCE_REMINDER'
  | 'COMMENT'
  | 'LIKE'
  | 'EXAM_FOLLOWUP_REMINDER'
  | 'SCORECARD_RELEASED'
  | 'EXAM_CREATED'
  | 'POST_CREATED'
  | 'UPCOMING_EXAM'
  | 'UPCOMING_EVENT'
  | 'ACADEMIC_YEAR_REMINDER'
  | 'RE_ENROLLMENT_IMMEDIATE_TRANSFER'
  | 'RE_ENROLLMENT_CYCLE_COMPLETED'
  | 'RE_ENROLLMENT_NEW_YEAR_ACTIVE'
  | 'TIMETABLE'
  | 'CLASSROOM_ASSIGNMENT'
  | 'GUARDIAN_LINK_REQUESTED';

/**
 * Enum for notification types (TypeScript enum for type safety)
 */
export enum NOTIFICATION_TYPE {
  EVENT = 'EVENT',
  MESSAGE = 'MESSAGE',
  ATTENDANCE = 'ATTENDANCE',
  INVOICE_REMINDER = 'INVOICE_REMINDER',
  INVOICE_OVERDUE_REMINDER = 'INVOICE_OVERDUE_REMINDER',
  STUDENT_AWARD = 'STUDENT_AWARD',
  ATTENDANCE_ABSENT = 'ATTENDANCE_ABSENT',
  ATTENDANCE_CHECK_IN = 'ATTENDANCE_CHECK_IN',
  ATTENDANCE_CHECK_OUT = 'ATTENDANCE_CHECK_OUT',
  ATTENDANCE_LATE_CHECK_IN = 'ATTENDANCE_LATE_CHECK_IN',
  ATTENDANCE_EARLY_CHECK_OUT = 'ATTENDANCE_EARLY_CHECK_OUT',
  STUDENT_ATTENDANCE_REMINDER = 'STUDENT_ATTENDANCE_REMINDER',
  COMMENT = 'COMMENT',
  LIKE = 'LIKE',
  EXAM_FOLLOWUP_REMINDER = 'EXAM_FOLLOWUP_REMINDER',
  SCORECARD_RELEASED = 'SCORECARD_RELEASED',
  EXAM_CREATED = 'EXAM_CREATED',
  POST_CREATED = 'POST_CREATED',
  UPCOMING_EXAM = 'UPCOMING_EXAM',
  UPCOMING_EVENT = 'UPCOMING_EVENT',
  ACADEMIC_YEAR_REMINDER = 'ACADEMIC_YEAR_REMINDER',
  RE_ENROLLMENT_IMMEDIATE_TRANSFER = 'RE_ENROLLMENT_IMMEDIATE_TRANSFER',
  RE_ENROLLMENT_CYCLE_COMPLETED = 'RE_ENROLLMENT_CYCLE_COMPLETED',
  RE_ENROLLMENT_NEW_YEAR_ACTIVE = 'RE_ENROLLMENT_NEW_YEAR_ACTIVE',
  TIMETABLE = 'TIMETABLE',
  CLASSROOM_ASSIGNMENT = 'CLASSROOM_ASSIGNMENT',
  GUARDIAN_LINK_REQUESTED = 'GUARDIAN_LINK_REQUESTED',
}

/**
 * Notification screen types from backend
 */
export type NotificationScreen =
  | 'Event-screen'
  | 'Invoice-screen'
  | 'Award-screen'
  | 'Attendance-screen'
  | 'MessageScreen'
  | 'Post-screen'
  | 'Exam-screen';

// Enum for notification screens (matches backend APP_NOTIFICATION_SCREENS)
export enum NOTIFICATION_SCREEN {
  EVENT = 'Event-screen',
  INVOICE = 'Invoice-screen',
  AWARD = 'Award-screen',
  ATTENDANCE = 'Attendance-screen',
  MESSAGE = 'MessageScreen',
  POST = 'Post-screen',
  EXAM = 'Exam-screen',
}

// MetaData for notification (matches backend INotificationData)
/**
 * Present when the backend stored a translation descriptor alongside the
 * notification. Notification text is composed server-side when the
 * notification is produced, so `title`/`message` are frozen in the producer's
 * language (English). When this is present we re-render from the key in the
 * reader's language instead (CVA-4602).
 */
export interface INotificationI18n {
  titleKey: string;
  bodyKey: string;
  params?: Record<string, string | number>;
}

export interface INotificationMetaData {
  screen: NOTIFICATION_SCREEN;
  i18n?: INotificationI18n;
  roleType?: string;
  studentId?: string;
  eventId?: string;
  eventName?: string;
  invoiceId?: string;
  assignInvoiceId?: string;
  dueDate?: string;
  postId?: string;
  postTitle?: string;
  commentId?: string;
  commenterName?: string;
  likerName?: string;
  examId?: string;
}

// Notification item (matches backend INotificationResponse)
export interface INotificationItem {
  id: string;
  createdAt: string;
  modifiedAt: string;
  title: string;
  message: string;
  type: string;
  isRead: boolean;
  metaData?: INotificationMetaData;
}

// Notification list response (matches backend INotificationListResponse)
export interface INotificationListResponse {
  notifications: INotificationItem[];
  total: number;
  totalUnread: number;
  page?: number;
  pageSize?: number;
}

// Mark notifications as read payload
export interface IMarkNotificationsReadPayload {
  notificationIds: string[];
}

// For thunk error typing
export type NotificationThunkError = IErrorResponse;
